import type { Analytics } from 'firebase/analytics';
import { getAnalytics } from 'firebase/analytics';
import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import type { Auth } from 'firebase/auth';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import type { FirebasePerformance } from 'firebase/performance';
import { getPerformance } from 'firebase/performance';
import type { FirebaseStorage } from 'firebase/storage';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

const hostname = window.location.hostname;
const isProduction = hostname === 'nft.runport.run';
const isLocalhost = hostname === 'localhost';

// ============================================
// 既存プロジェクト（dot99）- ソーシャルログイン用
// ============================================
const existingConfig = isProduction
  ? {
      apiKey: 'AIzaSyCoarf7vU6-vft-DTV4kgCa3WSg82kb-1c',
      authDomain: 'nft.runport.run',
      projectId: 'dot99-prod',
      storageBucket: 'dot99-prod.appspot.com',
      messagingSenderId: '281512048896',
      appId: '1:281512048896:web:920cde0fd6849dce909be5',
    }
  : {
      apiKey: 'AIzaSyCi7l6aVfynjTyA9DpaguIdTLGeQFhpGpY',
      authDomain: isLocalhost ? 'localhost' : 'dot99-dev.firebaseapp.com',
      projectId: 'dot99-dev',
      storageBucket: 'dot99-dev.appspot.com',
      messagingSenderId: '1065437798827',
      appId: '1:1065437798827:web:715fdb85b3fb4289acf2ee',
    };

// ============================================
// 新プロジェクト（heraldingera）- メインアプリ用
// ============================================
const newConfig = isProduction
  ? {
      apiKey: 'AIzaSyAZ6OOAN4IWrKSmAHw9IUI8onfNMSF39-c',
      authDomain: 'heraldingera-prod.firebaseapp.com',
      projectId: 'heraldingera-prod',
      storageBucket: 'heraldingera-prod.firebasestorage.app',
      messagingSenderId: '576736155706',
      appId: '1:576736155706:web:630a8fe337786dfd2f7d03',
      measurementId: 'G-T42R15W01X',
    }
  : {
      apiKey: 'AIzaSyDjB34GbN2JV4-s6ddMURGc0zVPdls7SCw',
      authDomain: 'heraldingera-dev.firebaseapp.com',
      projectId: 'heraldingera-dev',
      storageBucket: 'heraldingera-dev.firebasestorage.app',
      messagingSenderId: '919427751826',
      appId: '1:919427751826:web:e5142374c5dfc74c24eb4b',
      measurementId: 'G-VNC5NJFB2V',
    };

// ============================================
// reCAPTCHA v3 サイトキー（AppCheck用）
// ============================================
const recaptchaSiteKey = isProduction
  ? '6Lev4kIsAAAAAK-qfxV9DBrxnjKT20k5-paUSXrr'
  : '6Lc5zz4sAAAAAIMTGxzpzLzgUcgLUl9QJpHzH0GV';

// ============================================
// アプリ初期化
// ============================================

/** 既存プロジェクト（ソーシャルログイン用） */
export const existingApp: FirebaseApp = initializeApp(existingConfig, 'existing');

/** 新プロジェクト（メインアプリ用） */
export const app: FirebaseApp = initializeApp(newConfig);

// ============================================
// AppCheck 初期化（heraldingera のみ）
// ============================================
if (isLocalhost) {
  // ローカル開発時はデバッグトークンを使用
  // @ts-expect-error Firebase AppCheck debug token
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(recaptchaSiteKey),
  isTokenAutoRefreshEnabled: true,
});

// ============================================
// サービスインスタンス
// ============================================

/** 既存プロジェクトのAuth（ソーシャルログイン用） */
export const existingAuth: Auth = getAuth(existingApp);

/** 既存プロジェクトのFirestore（users、過去データ参照用） */
export const existingDb: Firestore = getFirestore(existingApp);

/** 新プロジェクトのAuth（カスタムトークン認証用） */
export const auth: Auth = getAuth(app);

/** 新プロジェクトのFirestore（メインDB） */
export const db: Firestore = getFirestore(app);

/** 新プロジェクトのStorage */
export const storage: FirebaseStorage = getStorage(app);

/** 既存プロジェクトのStorage（dot99のNFT参照用） */
export const existingStorage: FirebaseStorage = getStorage(existingApp);

/** 新プロジェクトのFunctions */
export const functions: Functions = getFunctions(app, 'asia-northeast1');

// ============================================
// Analytics & Performance（本番・開発環境のみ）
// ============================================
export let analytics: Analytics | null = null;
export let performance: FirebasePerformance | null = null;

if (!isLocalhost) {
  analytics = getAnalytics(app);
  performance = getPerformance(app);
}

// ============================================
// エミュレーター接続（localhost のみ）
// ============================================
if (isLocalhost) {
  // 既存プロジェクトのAuth
  connectAuthEmulator(existingAuth, 'http://localhost:9099', { disableWarnings: true });

  // 新プロジェクトのサービス
  connectAuthEmulator(auth, 'http://localhost:9098', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}
